/**
 * Content-based clustering interface.
 * Runs Jaccard + optional semantic clustering in a Web Worker with a 30s timeout.
 * Provides cluster assignments that can enhance spatial (Supercluster) clustering.
 */

import type { NormalizedEvent } from '@/sources/base-source';

/** Maps event ID → cluster ID (root event ID). */
export type ContentClusterMap = Map<string, string>;

/** Jaccard similarity threshold for title-based clustering. */
const JACCARD_THRESHOLD = 0.6;

/** Cosine similarity threshold for semantic refinement. */
const SEMANTIC_THRESHOLD = 0.75;

/** Maximum time to wait for worker response (ms). */
const WORKER_TIMEOUT_MS = 30_000;

let worker: Worker | null = null;
let pendingResolve: ((map: ContentClusterMap) => void) | null = null;
let pendingReject: ((err: Error) => void) | null = null;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;

/** Latest content cluster results. Empty map means no content clustering applied. */
let currentClusters: ContentClusterMap = new Map();

/** Monotonic version of currentClusters — incremented exactly where results
 *  LAND (every currentClusters assignment), never on promise resolution.
 *  A superseded request's promise can resolve with the WRONG result's
 *  arrival, so promise-based invalidation misses the last landing. */
let clusterVersion = 0;

/** Version of the current content-cluster results, for cache invalidation. */
export function getContentClusterVersion(): number {
  return clusterVersion;
}

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(
      new URL('../workers/clustering-worker.ts', import.meta.url),
      { type: 'module' },
    );
    worker.onmessage = handleWorkerMessage;
    worker.onerror = handleWorkerError;
  }
  return worker;
}

function handleWorkerMessage(e: MessageEvent): void {
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }

  if (e.data.type === 'result') {
    const map: ContentClusterMap = new Map();
    const clusters = e.data.clusters as Record<string, string>;
    for (const [id, root] of Object.entries(clusters)) {
      map.set(id, root);
    }
    currentClusters = map;
    clusterVersion++;
    pendingResolve?.(map);
  } else if (e.data.type === 'error') {
    console.warn('[Sentinel] Clustering worker error:', e.data.message);
    pendingReject?.(new Error(e.data.message));
  }

  pendingResolve = null;
  pendingReject = null;
}

function handleWorkerError(e: ErrorEvent): void {
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  console.warn('[Sentinel] Clustering worker error:', e.message);
  pendingReject?.(new Error(e.message));
  pendingResolve = null;
  pendingReject = null;
}

/**
 * Run content-based clustering on the given events.
 * Returns a map of event ID → cluster ID.
 * Times out after 30 seconds and returns empty map.
 *
 * @param events Events to cluster by title similarity
 * @param embeddings Optional embedding vectors for semantic refinement
 */
export function runContentClustering(
  events: NormalizedEvent[],
  embeddings?: { id: string; vector: number[] }[],
): Promise<ContentClusterMap> {
  // Reject any pending request
  if (pendingReject) {
    pendingReject(new Error('Superseded by new clustering request'));
    pendingResolve = null;
    pendingReject = null;
  }
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }

  // Skip clustering for very small datasets
  if (events.length < 10) {
    currentClusters = new Map();
    clusterVersion++;
    return Promise.resolve(currentClusters);
  }

  return new Promise<ContentClusterMap>((resolve, reject) => {
    pendingResolve = resolve;
    pendingReject = reject;

    // 30-second timeout
    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      console.warn('[Sentinel] Clustering worker timed out after 30s');
      // Terminate and recreate worker on timeout
      if (worker) {
        worker.terminate();
        worker = null;
      }
      pendingResolve = null;
      pendingReject = null;
      resolve(new Map()); // Return empty on timeout
    }, WORKER_TIMEOUT_MS);

    const w = getWorker();
    w.postMessage({
      type: 'cluster',
      events: events.map(e => ({ id: e.id, title: e.title })),
      threshold: JACCARD_THRESHOLD,
      embeddings: embeddings ?? [],
      semanticThreshold: SEMANTIC_THRESHOLD,
    });
  });
}

/** Get the latest content cluster map (synchronous). */
export function getContentClusters(): ContentClusterMap {
  return currentClusters;
}

/**
 * Check if two events belong to the same content cluster.
 * Falls back to false if no content clustering data is available.
 */
export function areSameContentCluster(eventIdA: string, eventIdB: string): boolean {
  const clusterA = currentClusters.get(eventIdA);
  const clusterB = currentClusters.get(eventIdB);
  if (!clusterA || !clusterB) return false;
  return clusterA === clusterB;
}

/** Terminate the worker and clean up. */
export function destroyContentClustering(): void {
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  if (worker) {
    worker.terminate();
    worker = null;
  }
  pendingResolve = null;
  pendingReject = null;
  currentClusters = new Map();
  clusterVersion++;
}
